// WebsiteChatbotLeadsPage.jsx — Hybrid Responsive (Desktop Table + Mobile Cards)

import React, { useState } from "react";
import {
  Container,
  Table,
  Button,
  Form,
  Modal,
  Row,
  Col,
  Card,
  Badge,
  Alert,
} from "react-bootstrap";

import {
  FaUser,
  FaEdit,
  FaTrash,
  FaFileAlt,
  FaDownload,
} from "react-icons/fa";

import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { websiteChatbotLeadsAPI } from "../services/api";
import "../styles/LeadManagement.css";

const WEBSITE_CHATBOT_LEADS_QUERY_KEY = ["websiteChatbotLeadsData"];

const DEFAULT_LEAD_STATE = {
  name: "",
  phone: "",
  address: "",
  issue: "",
  best_time_to_reach: "",
  summary: "",
  transcript: "",
  isqualified: false,
};

const formatDate = (s) => (s ? new Date(s).toLocaleDateString() : "Never");

/* ===========================================================
 * DESKTOP LEAD ROW (click to expand)
 * =========================================================== */
const LeadRow = ({
  lead,
  selectedIds,
  toggleSelect,
  handleEditLead,
  openDeleteModal,
  expanded,
  onToggleExpand,
}) => {
  return (
    <>
      {/* MAIN ROW */}
      <tr
        className={`lead-row-clickable ${expanded ? "table-active" : ""}`}
        onClick={() => onToggleExpand(lead)}
        style={{ cursor: "pointer" }}
      >
        <td onClick={(e) => e.stopPropagation()}>
          <Form.Check
            type="checkbox"
            checked={selectedIds.includes(lead.id)}
            onChange={() => toggleSelect(lead.id)}
          />
        </td>

        <td>
          <div className="d-flex align-items-center">
            <div className="lead-avatar me-2">
              {lead.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div>
              <div className="fw-bold">{lead.name || "Unknown"}</div>
              <small className="text-muted">{lead.phone || "No phone"}</small>
            </div>
          </div>
        </td>

        <td className="d-none d-md-table-cell">
          <div className="text-truncate" style={{ maxWidth: "150px" }}>
            {lead.issue || "No issue"}
          </div>
        </td>

        <td className="d-none d-lg-table-cell">
          <Badge bg={lead.isqualified ? "success" : "secondary"} className="badge-sm">
            {lead.isqualified ? "Qualified" : "Not Qualified"}
          </Badge>
        </td>

        <td className="d-none d-lg-table-cell">{formatDate(lead.created_at)}</td>

        <td onClick={(e) => e.stopPropagation()}>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handleEditLead(lead)}
            >
              <FaEdit />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => openDeleteModal(lead)}
            >
              <FaTrash />
            </Button>
          </div>
        </td>
      </tr>

      {/* EXPANDED DETAILS */}
      {expanded && (
        <tr>
          <td colSpan="6" className="p-0">
            <div className="expanded-content p-3 bg-light">
              <Row>
                <Col md={6}>
                  <h6><FaUser className="me-2" />Contact Details</h6>
                  <p><strong>Name:</strong> {lead.name || "Unknown"}</p>
                  <p><strong>Phone:</strong> {lead.phone || "Unknown"}</p>
                  <p><strong>Address:</strong> {lead.address || "Unknown"}</p>
                  <p><strong>Best Time to Reach:</strong> {lead.best_time_to_reach || "Unknown"}</p>
                </Col>
                <Col md={6}>
                  <h6><FaFileAlt className="me-2" />Issue & Summary</h6>
                  <p><strong>Issue:</strong> {lead.issue || "No issue specified"}</p>
                  <p><strong>Summary:</strong> {lead.summary || "No summary"}</p>
                  <p><strong>Qualified:</strong> {lead.isqualified ? "Yes" : "No"}</p>
                </Col>
              </Row>
              {lead.transcript && (
                <Row className="mt-3">
                  <Col>
                    <h6><FaFileAlt className="me-2" />Transcript</h6>
                    <div className="transcript-box p-2 bg-white rounded">
                      <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.9em" }}>
                        {lead.transcript}
                      </pre>
                    </div>
                  </Col>
                </Row>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

/* ===========================================================
 * MAIN COMPONENT
 * =========================================================== */
const WebsiteChatbotLeadsPage = () => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [deletingLead, setDeletingLead] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_LEAD_STATE);
  const [qualifiedFilter, setQualifiedFilter] = useState("all"); // "all", "qualified", "not_qualified"

  const queryClient = useQueryClient();

  // Fetch leads with pagination, search, and qualified filter
  const { data, isLoading, error } = useQuery({
    queryKey: [WEBSITE_CHATBOT_LEADS_QUERY_KEY, currentPage, pageSize, searchTerm, qualifiedFilter],
    queryFn: () =>
      websiteChatbotLeadsAPI.getLeads({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm || undefined,
        isqualified: qualifiedFilter === "all" ? undefined : qualifiedFilter === "qualified",
      }),
    keepPreviousData: true,
  });

  const leads = data?.results || [];
  const totalPages = Math.ceil((data?.count || 0) / pageSize);

  // Mutations
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => websiteChatbotLeadsAPI.updateLead(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(WEBSITE_CHATBOT_LEADS_QUERY_KEY);
      setShowEditModal(false);
      setEditingLead(null);
      setFormData(DEFAULT_LEAD_STATE);
      toast.success("Lead updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update lead: " + (error.response?.data?.detail || error.message));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: websiteChatbotLeadsAPI.deleteLead,
    onSuccess: () => {
      queryClient.invalidateQueries(WEBSITE_CHATBOT_LEADS_QUERY_KEY);
      setShowDeleteModal(false);
      setDeletingLead(null);
      setSelectedIds(selectedIds.filter(id => id !== deletingLead?.id));
      toast.success("Lead deleted successfully!");
    },
    onError: (error) => {
      toast.error("Failed to delete lead: " + (error.response?.data?.detail || error.message));
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      for (const id of selectedIds) {
        await websiteChatbotLeadsAPI.deleteLead(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(WEBSITE_CHATBOT_LEADS_QUERY_KEY);
      setSelectedIds([]);
      toast.success("Selected leads deleted successfully!");
    },
    onError: (error) => {
      toast.error("Failed to delete some leads");
    },
  });

  // Handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    setSelectedIds(
      selectedIds.length === leads.length ? [] : leads.map(lead => lead.id)
    );
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name || "",
      phone: lead.phone || "",
      address: lead.address || "",
      issue: lead.issue || "",
      best_time_to_reach: lead.best_time_to_reach || "",
      summary: lead.summary || "",
      transcript: lead.transcript || "",
      isqualified: lead.isqualified || false,
    });
    setShowEditModal(true);
  };

  const handleUpdateLead = () => {
    updateMutation.mutate({ id: editingLead.id, data: formData });
  };

  const openDeleteModal = (lead) => {
    setDeletingLead(lead);
    setShowDeleteModal(true);
  };

  const handleDeleteLead = () => {
    deleteMutation.mutate(deletingLead.id);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length > 0) {
      bulkDeleteMutation.mutate();
    }
  };

  const handleExportCSV = () => {
    // Create CSV content
    const headers = [
      "ID",
      "Name",
      "Phone",
      "Address",
      "Issue",
      "Summary",
      "Best Time to Reach",
      "Qualified",
      "Created At",
      "Conversation ID"
    ];

    const csvContent = [
      headers.join(","),
      ...leads.map(lead => [
        lead.id,
        `"${(lead.name || "").replace(/"/g, '""')}"`,
        `"${(lead.phone || "").replace(/"/g, '""')}"`,
        `"${(lead.address || "").replace(/"/g, '""')}"`,
        `"${(lead.issue || "").replace(/"/g, '""')}"`,
        `"${(lead.summary || "").replace(/"/g, '""')}"`,
        `"${(lead.best_time_to_reach || "").replace(/"/g, '""')}"`,
        lead.isqualified ? "Yes" : "No",
        new Date(lead.created_at).toLocaleDateString(),
        lead.conversation_id
      ].join(","))
    ].join("\n");

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `website_chatbot_leads_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onToggleExpand = (lead) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lead.id)) {
        newSet.delete(lead.id);
      } else {
        newSet.add(lead.id);
      }
      return newSet;
    });
  };

  const resetForm = () => {
    setFormData(DEFAULT_LEAD_STATE);
    setEditingLead(null);
  };

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          Error loading leads: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Website Chatbot Leads</h2>
          <small className="text-muted">Manage leads from your website chatbot</small>
        </div>
        <div className="d-flex gap-2">
          {selectedIds.length > 0 && (
            <Button variant="outline-danger" size="sm" onClick={handleBulkDelete}>
              <FaTrash className="me-1" />
              Delete Selected ({selectedIds.length})
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <Row className="mb-4">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Select
            value={qualifiedFilter}
            onChange={(e) => setQualifiedFilter(e.target.value)}
          >
            <option value="all">All Leads</option>
            <option value="qualified">Qualified Only</option>
            <option value="not_qualified">Not Qualified</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value={10}>10/page</option>
            <option value={25}>25/page</option>
            <option value={50}>50/page</option>
          </Form.Select>
        </Col>
        <Col md={3} className="d-flex justify-content-end">
          <Button variant="success" size="sm" onClick={handleExportCSV}>
            <FaDownload className="me-1" />
            Export CSV
          </Button>
        </Col>
      </Row>

      {/* Desktop Table */}
      <div className="d-none d-lg-block">
        <div className="table-responsive">
          <Table hover className="mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "40px" }}>
                  <Form.Check
                    type="checkbox"
                    checked={selectedIds.length === leads.length && leads.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Lead</th>
                <th>Issue</th>
                <th>Status</th>
                <th>Date</th>
                <th style={{ width: "100px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    <div className="text-muted">
                      <FaUser size={48} className="mb-3 opacity-50" />
                      <p>No leads found</p>
                      <small>Try adjusting your search or filters</small>
                    </div>
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <LeadRow
                    key={lead.id}
                    lead={lead}
                    selectedIds={selectedIds}
                    toggleSelect={toggleSelect}
                    handleEditLead={handleEditLead}
                    openDeleteModal={openDeleteModal}
                    expanded={expandedRows.has(lead.id)}
                    onToggleExpand={onToggleExpand}
                  />
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Tablet View - Simplified Table */}
      <div className="d-none d-md-block d-lg-none">
        <div className="table-responsive">
          <Table hover className="mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "40px" }}>
                  <Form.Check
                    type="checkbox"
                    checked={selectedIds.length === leads.length && leads.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Lead</th>
                <th>Status</th>
                <th style={{ width: "100px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="4" className="text-center py-5">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-5">
                    <div className="text-muted">
                      <FaUser size={48} className="mb-3 opacity-50" />
                      <p>No leads found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} className="lead-row-clickable">
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedIds.includes(lead.id)}
                        onChange={() => toggleSelect(lead.id)}
                      />
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="lead-avatar me-2">
                          {lead.name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <div className="fw-bold">{lead.name || "Unknown"}</div>
                          <small className="text-muted">{lead.phone || "No phone"}</small>
                          <div className="mt-1">
                            <small className="text-muted">{lead.issue?.substring(0, 30) || "No issue"}</small>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge bg={lead.isqualified ? "success" : "secondary"} className="badge-sm">
                        {lead.isqualified ? "Qualified" : "Not Qualified"}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditLead(lead)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => openDeleteModal(lead)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="d-md-none">
        {isLoading ? (
          <div className="text-center py-5">
            <LoadingSpinner />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-5">
            <div className="text-muted">
              <FaUser size={48} className="mb-3 opacity-50" />
              <p>No leads found</p>
              <small>Try adjusting your search or filters</small>
            </div>
          </div>
        ) : (
          <div className="row g-3">
            {leads.map((lead) => (
              <div key={lead.id} className="col-12">
                <Card className="lead-card h-100">
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-start mb-2">
                      <Form.Check
                        type="checkbox"
                        checked={selectedIds.includes(lead.id)}
                        onChange={() => toggleSelect(lead.id)}
                        className="me-2 mt-1"
                      />
                      <div className="lead-avatar me-2">
                        {lead.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="mb-1 fw-bold">{lead.name || "Unknown"}</h6>
                            <small className="text-muted d-block">{lead.phone || "No phone"}</small>
                          </div>
                          <Badge bg={lead.isqualified ? "success" : "secondary"} className="badge-sm">
                            {lead.isqualified ? "Qualified" : "Not Qualified"}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {lead.issue && (
                      <div className="mb-2">
                        <small className="text-muted fw-medium">Issue:</small>
                        <p className="mb-1 small">{lead.issue}</p>
                      </div>
                    )}

                    {lead.summary && (
                      <div className="mb-2">
                        <small className="text-muted fw-medium">Summary:</small>
                        <p className="mb-1 small">{lead.summary}</p>
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "No date"}
                      </small>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditLead(lead)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => openDeleteModal(lead)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              ‹ Prev
            </Button>
            <span className="text-muted small">
              {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next ›
            </Button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => { setShowEditModal(false); resetForm(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Issue</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.issue}
                onChange={(e) => setFormData({...formData, issue: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Best Time to Reach</Form.Label>
              <Form.Control
                type="text"
                value={formData.best_time_to_reach}
                onChange={(e) => setFormData({...formData, best_time_to_reach: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Summary</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.summary}
                onChange={(e) => setFormData({...formData, summary: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Transcript</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={formData.transcript}
                onChange={(e) => setFormData({...formData, transcript: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Qualified Lead"
                checked={formData.isqualified}
                onChange={(e) => setFormData({...formData, isqualified: e.target.checked})}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowEditModal(false); resetForm(); }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateLead} disabled={updateMutation.isLoading}>
            {updateMutation.isLoading ? <LoadingSpinner small /> : null}
            Update Lead
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the lead for <strong>{deletingLead?.name}</strong>?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteLead} disabled={deleteMutation.isLoading}>
            {deleteMutation.isLoading ? <LoadingSpinner small /> : null}
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default WebsiteChatbotLeadsPage;